import React from 'react';

// Detect YouTube/Vimeo URLs and return an embed URL; returns null for direct/unrecognised URLs
const getVideoEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?\s]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/(?:vimeo\.com\/)(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return null;
};

/**
 * SplitPanelContent — shared renderer for the split-page panel content.
 * Used by the form builder canvas preview, the builder's PreviewModal, and
 * the public form viewer. Renders image / video / rich-text HTML based on
 * the splitPanelConfig shape.
 *
 * Props:
 *   config  — splitPanelConfig object from form settings
 *   preview — true when shown in the builder (adds placeholder messaging)
 */
const SplitPanelContent = ({ config, preview = false }) => {
  const {
    contentType = 'image',
    imageUrl = '',
    imageObjectFit = 'cover',
    overlayCaption = '',
    videoUrl = '',
    richText = '',
  } = config || {};

  const empty = (msg) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        minHeight: 200,
        color: '#9ca3af',
        fontSize: 13,
        textAlign: 'center',
        padding: '16px',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      {preview && (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.35 }}>
          <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18" />
        </svg>
      )}
      <span style={{ maxWidth: 200 }}>{msg}</span>
    </div>
  );

  if (contentType === 'image') {
    if (!imageUrl) return empty(preview ? 'Paste an image URL in the Split Panel settings' : '');
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
        <img
          src={imageUrl}
          alt="Panel"
          style={{ width: '100%', height: '100%', objectFit: imageObjectFit || 'cover', display: 'block' }}
          onError={(e) => { e.currentTarget.style.opacity = '0.15'; }}
        />
        {overlayCaption && (
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.65))',
              padding: '32px 20px 16px',
              color: '#fff',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            {overlayCaption}
          </div>
        )}
      </div>
    );
  }

  if (contentType === 'video') {
    if (!videoUrl) return empty(preview ? 'Paste a YouTube, Vimeo, or video URL in the Split Panel settings' : '');
    const embedUrl = getVideoEmbedUrl(videoUrl);
    if (embedUrl) {
      return (
        <iframe
          src={embedUrl}
          style={{ width: '100%', height: '100%', border: 'none', display: 'block', minHeight: 200 }}
          allowFullScreen
          title="Split panel video"
        />
      );
    }
    const isVideoExt = /\.(mp4|webm|ogg|mov)(\?|$)/i.test(videoUrl);
    if (isVideoExt) {
      return (
        <video
          src={videoUrl}
          controls
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', minHeight: 200 }}
        />
      );
    }
    return empty('Could not embed video — use a YouTube, Vimeo, or direct .mp4 / .webm URL');
  }

  if (contentType === 'text') {
    if (!richText || richText === '<p><br></p>') return empty(preview ? 'Add text content in the Split Panel settings' : '');
    return (
      <div
        style={{
          padding: '28px 32px',
          height: '100%',
          overflowY: 'auto',
          lineHeight: 1.75,
          color: '#111827',
          fontSize: 15,
          boxSizing: 'border-box',
        }}
        // richText is sanitised HTML from react-quill; no user-supplied scripts reach here
        // because react-quill's delta-to-HTML converter never emits <script> tags.
        dangerouslySetInnerHTML={{ __html: richText }}
      />
    );
  }

  return empty(preview ? 'Select a content type in the Split Panel settings' : '');
};

export default SplitPanelContent;
